// =============================================================================
// Auth Store - Zustand store for dual-method authentication
// =============================================================================
// Phase 5: Frontend Implementation
// Supports phone (SMS) and email (OTP) authentication with state machine flow
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// =============================================================================
// Types
// =============================================================================

/**
 * Authentication flow steps (state machine)
 * IDLE → ENTER_IDENTIFIER → VERIFY_CODE → (AUTHENTICATED | SIGNUP → AUTHENTICATED)
 */
export type AuthStep =
  | 'IDLE'
  | 'ENTER_IDENTIFIER'
  | 'VERIFY_CODE'
  | 'SIGNUP'
  | 'AUTHENTICATED';

export type IdentifierType = 'phone' | 'email';

export interface PendingIdentifier {
  type: IdentifierType;
  value: string;
  maskedValue?: string;
}

export interface AuthUser {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  primaryContactMethod: 'email' | 'phone';
  userType: 'AGENT' | 'TRANSACTION_COORDINATOR' | 'ADMIN';
  avatarUrl?: string;
  companyName?: string;
  licenseNumber?: string;
  isActive: boolean;
  lastLoggedIn?: string;
  hasDocuSignConnected?: boolean;
  // Password auth v1 (2026-09): derived flags from /auth/me
  hasPassword?: boolean;
  mfaEnabled?: boolean;
  notificationPrefs?: {
    packageActivity: boolean;
  };
  uploadPrefs?: {
    aiNaming: boolean;
  };
  createdAt: string;
  updatedAt: string;
  termsAcceptedVersion?: string;
}

export interface AuthBilling {
  plan: 'FREE' | 'PRO' | 'ENTERPRISE';
  status: 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'TRIALING';
  trialEnd?: string;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string;
}

export interface AuthOrganization {
  _id: string;
  name: string;
  type: 'INDIVIDUAL' | 'TEAM' | 'BROKERAGE';
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  billing?: AuthBilling;
}

// Legacy membership type for backwards compatibility
export interface AuthMembership {
  _id: string;
  userId: string;
  organizationId: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  joinedAt?: string;
}

// =============================================================================
// Store Interface
// =============================================================================

/** Session diagnostics state (internal debugging) */
export interface DiagState {
  active: boolean;
  _srcName?: string;
  _refName?: string;
  _refEmail?: string;
  _ts?: string;
  _exp?: string;
  _rm?: number;
}

interface AuthState {
  // Flow state
  step: AuthStep;
  pendingIdentifier: PendingIdentifier | null;
  signupToken: string | null;
  signupTokenExpiresAt: string | null;
  verifiedIdentifier: string | null;
  verifiedIdentifierType: IdentifierType | null;

  // v5.1: Return URL for post-auth redirect (e.g., shared property page)
  returnTo: string | null;

  // v5.2: Pre-fill signup form with guest data (name/email from Gatekeeper)
  guestPreFill: { name: string; email: string } | null;

  // User state
  user: AuthUser | null;
  organization: AuthOrganization | null;

  // Per-environment feature flags from /auth/me. NOT org entitlements — these
  // are on/off per deploy (e.g. formsBridge hidden in prod until it ships).
  // Set once at bootstrap via setFeatures so profile-edit re-auths that call
  // setAuthenticated don't wipe them.
  features: { formsBridge: boolean };

  // Legacy aliases (for backwards compatibility)
  activeOrganization: AuthOrganization | null;
  membership: AuthMembership | null;

  // Session diagnostics (internal)
  _diag: DiagState | null;
  _dx: boolean;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Actions
  setStep: (step: AuthStep) => void;
  setPendingIdentifier: (identifier: PendingIdentifier | null) => void;
  setSignupToken: (
    token: string | null,
    expiresAt?: string | null,
    verifiedIdentifier?: string | null,
    verifiedIdentifierType?: IdentifierType | null
  ) => void;
  setAuthenticated: (user: AuthUser, organization: AuthOrganization) => void;
  setFeatures: (features: Partial<AuthState['features']>) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  // v5.1: Set return URL for post-auth redirect
  setReturnTo: (url: string | null) => void;
  // v5.1: Get and clear returnTo (consume it)
  consumeReturnTo: () => string | null;
  // v5.2: Set guest pre-fill data for signup form
  setGuestPreFill: (data: { name: string; email: string } | null) => void;
  logout: () => void;
  reset: () => void;

  // Legacy actions (for backwards compatibility)
  clearAuth: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  switchOrganization: (org: any, membership?: any) => void;

  // Computed helpers
  isAuthenticated: () => boolean;

  // User update
  updateUser: (updates: Partial<AuthUser>) => void;

  // Session diagnostics (internal)
  setDiag: (diag: DiagState | null, dx?: boolean) => void;
}

// =============================================================================
// Initial State
// =============================================================================

const initialState = {
  step: 'IDLE' as AuthStep,
  pendingIdentifier: null,
  signupToken: null,
  signupTokenExpiresAt: null,
  verifiedIdentifier: null,
  verifiedIdentifierType: null,
  returnTo: null,
  guestPreFill: null,
  user: null,
  organization: null,
  features: { formsBridge: false },
  activeOrganization: null,
  membership: null,
  _diag: null as DiagState | null,
  _dx: false,
  isLoading: false,
  error: null,
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Create a membership object from organization and user
 */
function createMembershipFromOrg(
  org: AuthOrganization,
  userId: string
): AuthMembership {
  return {
    _id: `membership-${org._id}`,
    userId,
    organizationId: org._id,
    role: org.role,
    joinedAt: new Date().toISOString(),
  };
}

// =============================================================================
// Store
// =============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setStep: (step) => set({ step, error: null }),

      setPendingIdentifier: (identifier) =>
        set({
          pendingIdentifier: identifier,
          step: identifier ? 'VERIFY_CODE' : get().step,
          error: null,
        }),

      setSignupToken: (token, expiresAt, verifiedIdentifier, verifiedIdentifierType) =>
        set({
          signupToken: token,
          signupTokenExpiresAt: expiresAt ?? null,
          verifiedIdentifier: verifiedIdentifier ?? null,
          verifiedIdentifierType: verifiedIdentifierType ?? null,
          step: token ? 'SIGNUP' : get().step,
          error: null,
        }),

      setFeatures: (features) =>
        set((state) => ({ features: { ...state.features, ...features } })),

      setAuthenticated: (user, organization) => {
        const membership = createMembershipFromOrg(organization, user._id);
        set({
          user,
          organization,
          activeOrganization: organization, // Legacy alias
          membership, // Legacy alias
          step: 'AUTHENTICATED',
          // Clear transient auth flow data
          pendingIdentifier: null,
          signupToken: null,
          signupTokenExpiresAt: null,
          verifiedIdentifier: null,
          verifiedIdentifierType: null,
          isLoading: false,
          error: null,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error, isLoading: false }),

      // v5.1: Set return URL for post-auth redirect
      setReturnTo: (url) => set({ returnTo: url }),

      // v5.1: Get and clear returnTo (consume it once)
      consumeReturnTo: () => {
        const { returnTo } = get();
        if (returnTo) {
          set({ returnTo: null });
        }
        return returnTo;
      },

      // v5.2: Set guest pre-fill data for signup form
      setGuestPreFill: (data) => set({ guestPreFill: data }),

      logout: () => {
        set({
          ...initialState,
          step: 'IDLE',
        });
      },

      reset: () => set(initialState),

      // Legacy alias for logout
      clearAuth: () => {
        set({
          ...initialState,
          step: 'IDLE',
        });
      },

      // Legacy method for switching organization
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      switchOrganization: (org: any, membership?: any) => {
        const { user } = get();
        // Extract role from membership if org doesn't have it
        const role = org.role || membership?.role || 'MEMBER';
        const authOrg: AuthOrganization = {
          _id: org._id,
          name: org.name,
          type: org.type,
          role,
        };
        const newMembership: AuthMembership | null = membership
          ? {
              _id: membership._id,
              userId: membership.userId,
              organizationId: membership.organizationId,
              role: membership.role,
              joinedAt: membership.joinedAt,
            }
          : user
            ? createMembershipFromOrg(authOrg, user._id)
            : null;
        set({
          organization: authOrg,
          activeOrganization: authOrg,
          membership: newMembership,
        });
      },

      isAuthenticated: () => {
        const { user, organization, step } = get();
        return step === 'AUTHENTICATED' && user !== null && organization !== null;
      },

      updateUser: (updates: Partial<AuthUser>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },

      setDiag: (diag, dx) => set({
        _diag: diag,
        ...(dx !== undefined && { _dx: dx }),
      }),
    }),
    {
      name: 'transakt-auth',
      // Version 4: Added billing to AuthOrganization
      // Increment this when making breaking changes to force localStorage reset
      version: 4,
      partialize: (state) => ({
        // Persist authenticated state
        user: state.user,
        organization: state.organization,
        activeOrganization: state.organization,
        membership: state.membership,
        step: state.step === 'AUTHENTICATED' ? 'AUTHENTICATED' : 'IDLE',
        // Persist across page navigation (Login → Verify → Signup)
        returnTo: state.returnTo,
        guestPreFill: state.guestPreFill,
      }),
      // Migration: preserve authenticated state from older versions
      migrate: (persistedState: unknown, version: number) => {
        if (version < 4) {
          const old = persistedState as Record<string, unknown> | null;
          return {
            step: (old?.step as string) === 'AUTHENTICATED' ? 'AUTHENTICATED' as const : 'IDLE' as const,
            pendingIdentifier: null,
            signupToken: null,
            signupTokenExpiresAt: null,
            verifiedIdentifier: null,
            verifiedIdentifierType: null,
            returnTo: null,
            guestPreFill: null,
            user: (old?.user as AuthUser | null) ?? null,
            organization: (old?.organization as AuthOrganization | null) ?? null,
            activeOrganization: (old?.activeOrganization as AuthOrganization | null) ?? null,
            membership: (old?.membership as AuthMembership | null) ?? null,
            isLoading: false,
            error: null,
          };
        }
        return persistedState as AuthState;
      },
    }
  )
);

// =============================================================================
// Selectors (for convenient access)
// =============================================================================

export const selectUser = (state: AuthState) => state.user;
export const selectOrganization = (state: AuthState) => state.organization;
export const selectActiveOrganization = (state: AuthState) => state.activeOrganization;
export const selectMembership = (state: AuthState) => state.membership;
export const selectStep = (state: AuthState) => state.step;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectError = (state: AuthState) => state.error;
export const selectPendingIdentifier = (state: AuthState) => state.pendingIdentifier;
export const selectSignupToken = (state: AuthState) => state.signupToken;
